/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/naming-convention */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AllowanceBaggageIcon,
  CancelFareIcon,
  ChangeFareIcon,
  ChangeFareInfo,
  ChooseSeatIcon,
  DateRangeIcon,
  LuggageIcon,
  LunchIcon,
  PrioryIcon,
  RefundIcon,
  ScoreRatioIcon,
  WaitingRoomIcon,
} from 'public/icons/trip-detail-icon';
import React from 'react';
import { useBoolean } from '@/utils/hooks';
import { TFareRuleV2 } from '@/slices/flightData/slices';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { style } from '@/styles/globals';
import { checkRuleEnabled } from '@/utils/checkRuleEnabled';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { pr } from '@/utils/pxToRem';
import { toVND } from '@/utils/currency';
import { extractRefundAndUpdate } from '@/utils/extractRefundAndUpdate';
import { format } from 'date-fns';
import useCheckRefund from '@/utils/hooks/use-check-refund';
import { isMealCheck } from '@/utils/checkMeal';
import { checkIsTet } from '@/utils/isTet';
import { AddonMappingReturnV2 } from '@/utils/addonMapping';
import { useAppSelector } from '@/utils/hook';

const Container = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.white2,
  borderRadius: theme.shape.borderRadius,
  padding: theme.palette.medium,
}));

const AddonContainer = styled('div')`
  ul {
    list-style-type: none;
  }
`;

interface AddonsDetailProps {
  selectedFlightProps: any,
  fareRule: TFareRuleV2;
  duration: number;
  onViewRefundRule: () => void;
  onViewUpdateRule: () => void;
}

const AddonsDetail: React.FC<AddonsDetailProps> = ({
  selectedFlightProps,
  fareRule,
  duration,
  onViewRefundRule,
  onViewUpdateRule,
}) => {
  const fareConfig = useAppSelector((state) => state.fareRule.fareConfig);
  return (
    <Stack>
      <Divider sx={{ my: () => pr`16px` }} />
      <Stack
        alignItems="baseline"
        justifyContent="space-between"
        direction="row"
      >
        <Typography variant="contentBold">{fareRule.detailVi}</Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: (theme) => theme.palette.medium,
          }}
        >
          <Typography
            color={(theme) => theme.palette.product3}
            variant="captionMedium"
          >
            Đổi hạng vé
          </Typography>
          <ArrowForwardIosIcon
            sx={{
              fontSize: '12px',
              color: (theme) => theme.palette.product3,
            }}
          />
        </Box>
      </Stack>
      <Typography
        mb={() => pr`8px`}
        fontWeight={400}
        variant="captionMedium"
      >
        Bạn có thể
        {' '}
        <strong> mua thêm hành lý, suất ăn và các tiện ích khác </strong>
        {' '}
        sau khi
        nhập thông tin hành khách
      </Typography>

      <Container spacing={2}>
        {!fareRule ? (
          <SkeletonList />
        ) : (
          <AddonContainer>
            {mappingRulesText(
              selectedFlightProps,
              fareRule,
              duration,
              onViewRefundRule,
              onViewUpdateRule,
              fareConfig,
            )}
          </AddonContainer>
        )}
      </Container>
    </Stack>
  );
};

interface RuleItemProps {
  icon: any;
  text: string;
  isHaveDetail?: boolean;
  onClickViewDetail?: () => void;
}

const RuleItem: React.FC<RuleItemProps> = ({
  icon,
  text,
  isHaveDetail,
  onClickViewDetail,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0}
  >
    <ListItemIcon
      sx={{
        mr: (theme) => theme.palette.xxSmall,
        minWidth: '25px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {icon}
    </ListItemIcon>
    <Stack
      spacing="0.5rem"
      direction="row"
      alignItems="baseline"
    >
      <Typography variant="contentRegular">{text}</Typography>
      {isHaveDetail && (
        <Typography
          onClick={onClickViewDetail}
          variant="captionRegular"
          color={(theme) => theme.palette.product3}
          sx={{ textDecoration: 'underline' }}
        >
          <span style={{ textDecoration: 'underline' }}>Xem chính sách</span>
        </Typography>
      )}
    </Stack>
  </Stack>
);

function mappingRulesText(
  selectedFlight: any,
  fareRule: TFareRuleV2,
  duration: number,
  onViewRefundRule: () => void,
  onViewUpdateRule: () => void,
  fareConfig: AddonMappingReturnV2[],
) {
  const {
    isBaggage,
    baggageWeight,
    baggageQuantity,
    isAllowanceBaggage,
    allowanceBaggageQuantity,
    allowanceBaggageWeight,
    mealDuration,
    isMeal,
    isRefund1,
    isRefund2,
    isUpdateTicket1,
    isUpdateTicket2,
    isUpdateName,
    isSelectSeat,
    updateNameFee,
    isDropOutRoute,
    feeDropOutRoute,
    isUpdateRouteAtAirport,
    feeUpdateRouteAtAirport,
    hasBusinessRoom,
    hasPriorityCounters,
    hasPlusPoint,
    operatingAirline,
  } = fareRule;
  const [isViewMore, setIsViewMore] = useBoolean(false);
  const dateExpire = format(
    new Date(selectedFlight?.route?.departureDate as any),
    'yyyy/MM/dd',
  );
  const timeExpire = selectedFlight?.route?.departureTime;

  const fareClass = selectedFlight?.segments?.[0]?.class;
  const airline = selectedFlight?.airline;
  const endPoint = selectedFlight?.segments?.[0]?.endPoint;
  const startPoint = selectedFlight?.segments?.[0]?.startPoint;
  const cabin = selectedFlight?.segments?.[0]?.cabin;
  const {
    canRefund1,
    canRefund2,
    canSupportRefund1,
    canSupportRefund2,
    refundExpireDateTime1,
    refundExpireDateTime2,
    canUpdate1,
    canUpdate2,
    canSupportUpdate1,
    canSupportUpdate2,
    updateExpireDateTime1,
    updateExpireDateTime2,
  } = extractRefundAndUpdate({
    airline: operatingAirline || '',
    expireDate: dateExpire || '',
    expireTime: timeExpire || '',
    fareClass: fareClass || '',
    fareConfig: fareConfig || [],
    isTetDay: checkIsTet(selectedFlight?.date),
    cabin,
  });
  const { type: typeRefund, expireDateTime: expireDateTimeRefund } = useCheckRefund(canRefund1, canRefund2, refundExpireDateTime1 || new Date(), refundExpireDateTime2 || new Date(), canSupportRefund1, canSupportRefund2);
  const canRefund = !['pending-reject', 'reject-reject', 'reject-all'].includes(typeRefund) && expireDateTimeRefund;
  const { type: typeUpdate, expireDateTime: expireDateTimeUpdate } = useCheckRefund(canUpdate1, canUpdate2, updateExpireDateTime1 || new Date(), updateExpireDateTime2 || new Date(), canSupportUpdate1, canSupportUpdate2);
  const canUpdate = !['pending-reject', 'reject-reject', 'reject-all'].includes(typeUpdate) && expireDateTimeUpdate;
  // const canUpdateTicket = checkRuleEnabled(isUpdateTicket1) || checkRuleEnabled(isUpdateTicket2);

  const ruleCompArr = [];

  if (checkRuleEnabled(isBaggage) && baggageWeight && baggageQuantity) {
    const text = `${baggageWeight}kg hành lý xách tay (${baggageQuantity} kiện)`;
    ruleCompArr.push(<RuleItem
      icon={<LuggageIcon />}
      text={text}
      key={text}
    />);
  }

  if (
    checkRuleEnabled(isAllowanceBaggage)
    && allowanceBaggageWeight
    && allowanceBaggageQuantity
  ) {
    const text = `${allowanceBaggageWeight}kg hành lý ký gửi (${allowanceBaggageQuantity} kiện)`;
    ruleCompArr.push(<RuleItem
      icon={<AllowanceBaggageIcon />}
      text={text}
    />);
  } else {
    const text = 'Không bao gồm hành lý ký gửi';
    ruleCompArr.push(
      <RuleItem
        icon={<AllowanceBaggageIcon color={style.color.red3} />}
        text={text}
      />,
    );
  }

  if (
    isMealCheck(operatingAirline, duration, mealDuration, isMeal)) {
    const text = 'Đã bao gồm suất ăn';
    ruleCompArr.push(<RuleItem
      icon={<LunchIcon />}
      text={text}
    />);
  } else {
    const text = 'Không bao gồm suất ăn';
    ruleCompArr.push(
      <RuleItem
        icon={<LunchIcon color={style.color.red3} />}
        text={text}
      />,
    );
  }

  if (canRefund) {
    const text = 'Được phép hoàn vé';
    ruleCompArr.push(
      <RuleItem
        icon={<RefundIcon />}
        isHaveDetail
        onClickViewDetail={onViewRefundRule}
        text={text}
      />,
    );
  } else {
    const text = 'Không được phép hoàn vé';
    ruleCompArr.push(
      <RuleItem
        isHaveDetail
        onClickViewDetail={onViewRefundRule}
        icon={<RefundIcon color={style.color.red3} />}
        text={text}
      />,
    );
  }

  if (canUpdate) {
    const text = 'Được phép đổi vé';
    ruleCompArr.push(
      <RuleItem
        icon={<DateRangeIcon />}
        isHaveDetail
        onClickViewDetail={onViewUpdateRule}
        text={text}
      />,
    );
  } else {
    const text = 'Không được phép đổi vé';
    ruleCompArr.push(
      <RuleItem
        isHaveDetail
        onClickViewDetail={onViewUpdateRule}
        icon={<DateRangeIcon color={style.color.red3} />}
        text={text}
      />,
    );
  }

  if (isSelectSeat === 'Miễn phí') {
    const text = 'Miễn phí chọn chỗ ngồi';
    ruleCompArr.push(<RuleItem
      icon={<ChooseSeatIcon />}
      text={text}
    />);
  }

  if (checkRuleEnabled(isDropOutRoute) && feeDropOutRoute) {
    const text = `Bỏ chuyến mất phí ${toVND(+feeDropOutRoute)}`;
    ruleCompArr.push(<RuleItem
      icon={<CancelFareIcon />}
      text={text}
    />);
  }
  if (
    checkRuleEnabled(isUpdateRouteAtAirport)
    && feeUpdateRouteAtAirport
    && !Number.isNaN(+feeUpdateRouteAtAirport)
  ) {
    let text = 'Đổi chuyến tại sân bay mất phí ';
    text += toVND(+feeUpdateRouteAtAirport);
    ruleCompArr.push(<RuleItem
      icon={<ChangeFareIcon />}
      text={text}
    />);
  }
  if (checkRuleEnabled(hasPriorityCounters)) {
    const text = 'Quầy thủ tục ưu tiên';
    ruleCompArr.push(<RuleItem
      icon={<PrioryIcon />}
      text={text}
    />);
  }

  if (checkRuleEnabled(hasBusinessRoom)) {
    const text = 'Phòng chờ thương gia';
    ruleCompArr.push(<RuleItem
      icon={<WaitingRoomIcon />}
      text={text}
    />);
  }
  if (hasPlusPoint.toString() !== 'false' && hasPlusPoint.toString() !== '0%') {
    const text = `Hệ số cộng điểm: ${hasPlusPoint === 'No' ? 'Không' : hasPlusPoint
    }`;
    ruleCompArr.push(<RuleItem
      icon={<ScoreRatioIcon />}
      text={text}
    />);
  }
  if (checkRuleEnabled(isUpdateName) && updateNameFee) {
    const text = `Thay đổi tên mất phí ${toVND(+updateNameFee)}`;
    ruleCompArr.push(<RuleItem
      icon={<ChangeFareInfo />}
      text={text}
    />);
  }

  return (
    <>
      <Stack spacing="1em">
        <Stack
          style={{
            height: !isViewMore && ruleCompArr.length > 5 ? '170px' : 'auto',
            overflow: 'hidden',
          }}
          spacing={pr`16px`}
        >
          {ruleCompArr}
        </Stack>
        {ruleCompArr.length > 5 && (
          <Stack>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              onClick={setIsViewMore.toggle}
            >
              <Typography
                variant="contentRegular"
                color={(theme) => theme.palette.product3}
                sx={{ textDecoration: 'underline' }}
              >
                <span style={{ textDecoration: 'underline' }}>
                  {!isViewMore ? 'Tiện ích khác' : 'Thu gọn'}
                </span>
              </Typography>
              {!isViewMore ? (
                <KeyboardArrowDownIcon
                  sx={{ color: (theme) => theme.palette.product3 }}
                />
              ) : (
                <KeyboardArrowUpIcon
                  sx={{ color: (theme) => theme.palette.product3 }}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </>
  );
}

const SkeletonList = () => (
  <List>
    {Array.from(Array(6).keys()).map((x) => (
      <ListItem key={x}>
        <ListItemAvatar>
          <Skeleton
            animation="wave"
            variant="circular"
            width={40}
            height={40}
          />
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton animation="wave" />}
          secondary={<Skeleton animation="wave" />}
        />
      </ListItem>
    ))}
  </List>
);

export default AddonsDetail;
